import React from 'react';
import { Route } from 'react-router-dom';
import InventarioLayout from './InventarioLayout';
import TiposList from './pages/TiposList';
import ActivosList from './pages/ActivosList';
import HistorialGlobal from './pages/HistorialGlobal';
import Compras from './pages/Compras';
import Ajustes from './pages/Ajustes';
import {
  InvGuard,
  InvTipoGuard,
  InvTipoCompat,
  InventarioLanding,
} from './InventarioAccess';
import { AMBITOS } from './utils/rutaTipo';

/**
 * Rutas del módulo Inventario.
 *
 * Estructura:
 *   /inventario                        → Dashboard
 *   /inventario/tipos                  → TiposList (gestión de tipos)
 *   /inventario/compras                → Compras (stock entries)
 *   /inventario/historial              → HistorialGlobal
 *   /inventario/ajustes                → Ajustes (avisos: umbrales + reglas)
 *   /inventario/interno/:slug          → ActivosList (activos de un tipo)
 *   /inventario/interno/:slug/:id      → ActivosList con drawer abierto
 *   /inventario/colab/:slug[/:id]      → ídem en el ámbito colaboradores
 *   /inventario/:slug[/:id]            → COMPAT: redirige al ámbito que
 *                                        corresponda (InvTipoCompat)
 *
 * EL ÁMBITO VA EN LA URL, Y VA COMO SEGMENTO LITERAL:
 *   El slug no identifica un tipo (inv_asset_types es UNIQUE (scope,
 *   slug)), así que sin ámbito en la ruta dos entradas de menú
 *   distintas apuntaban al mismo sitio: se pintaba un ámbito
 *   indeterminado y el subsidebar resaltaba las dos.
 *
 *   Los segmentos son literales ('interno' / 'colab') y NO un
 *   parámetro `:scope`. Con `:scope/:slug` habría empate de ranking en
 *   React Router v6 contra la ruta de compatibilidad `:slug/:id` (dos
 *   segmentos dinámicos cada una) y un deep-link antiguo como
 *   /inventario/movil/13 podría resolverse como scope='movil'. Con
 *   segmentos estáticos no hay ambigüedad posible: lo estático gana.
 *
 *   El ámbito llega al componente como PROP (no por useParams), que es
 *   la consecuencia de usar segmentos literales.
 *
 * IMPORTANTE — orden de rutas en React Router v6:
 *   Las rutas estáticas (tipos, compras, historial) tienen precedencia
 *   automática sobre la dinámica :slug. No hay conflicto.
 *
 * USO desde el router principal del proyecto:
 *
 *   import { inventarioRoutes } from './assets/componentes/inventario/routes';
 *
 *   <Routes>
 *     ...rutas existentes...
 *     {inventarioRoutes}
 *   </Routes>
 *
 * Si el proyecto usa createBrowserRouter (data router) en lugar de
 * <Routes>, se importa `inventarioRouteObject` (también exportado).
 */

// Las dos rutas (listado y listado+drawer) de un ámbito concreto.
// `ambito` es el segmento literal, que además se pasa como prop: es la
// única fuente del ámbito para el guard y para la pantalla.
const rutasDeAmbito = (ambito) => [
  <Route
    key={`${ambito}-slug`}
    path={`${ambito}/:slug`}
    element={<InvTipoGuard scope={ambito}><ActivosList scope={ambito} /></InvTipoGuard>}
  />,
  <Route
    key={`${ambito}-slug-id`}
    path={`${ambito}/:slug/:id`}
    element={<InvTipoGuard scope={ambito}><ActivosList scope={ambito} /></InvTipoGuard>}
  />,
];

// Variante JSX: para proyectos que usan <Routes>
export const inventarioRoutes = (
  <Route path="/inventario" element={<InventarioLayout />}>
    <Route index element={<InventarioLanding />} />
    <Route path="tipos" element={<InvGuard anyOf={['inventario.tipos.view']}><TiposList /></InvGuard>} />
    <Route path="compras" element={<InvGuard anyOf={['inventario.stock-entries.view']}><Compras /></InvGuard>} />
    <Route path="historial" element={<InvGuard anyOf={['inventario.eventos.view']}><HistorialGlobal /></InvGuard>} />
    <Route path="ajustes" element={<InvGuard superOnly><Ajustes /></InvGuard>} />
    {AMBITOS.map((a) => rutasDeAmbito(a))}
    {/* Compatibilidad: enlaces sin ámbito (favoritos y los `link` ya
        guardados en notificaciones_inventario). Redirigen al ámbito donde
        el usuario tenga ese slug. Las rutas de arriba son estáticas, así
        que ganan el ranking de v6 y estas solo entran cuando el primer
        segmento no es un ámbito. */}
    <Route path=":slug" element={<InvTipoCompat />} />
    <Route path=":slug/:id" element={<InvTipoCompat />} />
  </Route>
);

// Variante objeto: para proyectos que usan createBrowserRouter
export const inventarioRouteObject = {
  path: '/inventario',
  element: <InventarioLayout />,
  children: [
    { index: true, element: <InventarioLanding /> },
    { path: 'tipos', element: <InvGuard anyOf={['inventario.tipos.view']}><TiposList /></InvGuard> },
    { path: 'compras', element: <InvGuard anyOf={['inventario.stock-entries.view']}><Compras /></InvGuard> },
    { path: 'historial', element: <InvGuard anyOf={['inventario.eventos.view']}><HistorialGlobal /></InvGuard> },
    { path: 'ajustes', element: <InvGuard superOnly><Ajustes /></InvGuard> },
    ...AMBITOS.flatMap((a) => [
      {
        path: `${a}/:slug`,
        element: <InvTipoGuard scope={a}><ActivosList scope={a} /></InvTipoGuard>,
      },
      {
        path: `${a}/:slug/:id`,
        element: <InvTipoGuard scope={a}><ActivosList scope={a} /></InvTipoGuard>,
      },
    ]),
    // Compatibilidad de las rutas sin ámbito (ver variante JSX).
    { path: ':slug', element: <InvTipoCompat /> },
    { path: ':slug/:id', element: <InvTipoCompat /> },
  ],
};

// Default export por comodidad
export default inventarioRoutes;
